import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import { googleMapsLoaderOptions } from "../../../config/googleMaps";

type DevicePoint = {
  id: string | number;
  lat: number;
  lng: number;
  username: string;
  id_device?: string | number;
  time?: string;
  battery?: number | string;
  utc?: string | number;
};

interface AllDevicesMapProps {
  devices: DevicePoint[];
  defaultCenter: { lat: number; lng: number };
  mapContainerStyle: React.CSSProperties;
  getCustomIcon: (name: string) => string;
  formatDateTime?: (value: string) => string;
}

const parseAsUTC = (iso: string) => {
  const s = String(iso);
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  return new Date(s + "Z");
};

const parseUtcOffsetHours = (raw: number | string) => {
  if (typeof raw === "number") return raw;
  const s = String(raw).trim();
  const m = s.match(/^([+-])?(\d{1,2})(?::(\d{2}))?$/); // ±HH[:MM]
  if (m) {
    const sign = m[1] === "-" ? -1 : 1;
    const hh = Number(m[2] || 0);
    const mm = Number(m[3] || 0);
    return sign * (hh + mm / 60);
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const formatUtcLabel = (raw: number | string) => {
  const n = parseUtcOffsetHours(raw);
  const sign = n >= 0 ? "+" : "-";
  const abs = Math.abs(n);
  const hours = Math.trunc(abs);
  const minutes = Math.round((abs - hours) * 60);
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
};

const computeTimePlusUtc = (timeIso: string, utcOffset: number | string) => {
  const baseUtc = parseAsUTC(timeIso);
  const nHours = parseUtcOffsetHours(utcOffset); // ⬅️ pakai parser baru
  const shifted = new Date(baseUtc.getTime() + nHours * 3600_000);

  const display_enUS = shifted.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "UTC",
  });
  const display_idID = shifted.toLocaleString("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
  });

  return {
    display_enUS,
    display_idID,
    iso: shifted.toISOString(),
    utcLabel: formatUtcLabel(utcOffset),
  };
};

const AllDevicesMap: React.FC<AllDevicesMapProps> = ({
  devices,
  defaultCenter,
  mapContainerStyle,
  getCustomIcon,
  formatDateTime,
}) => {
  const { isLoaded: googleLoaded } = useJsApiLoader(googleMapsLoaderOptions);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [activeId, setActiveId] = useState<string | number | null>(null);

  const validDevices = useMemo(
    () =>
      (devices || []).filter(
        (d) =>
          typeof d.lat === "number" &&
          typeof d.lng === "number" &&
          !Number.isNaN(d.lat) &&
          !Number.isNaN(d.lng),
      ),
    [devices],
  );

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (validDevices.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      validDevices.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
      map.fitBounds(bounds);
    }
  };

  useEffect(() => {
    if (!googleLoaded || !mapRef.current) return;
    if (validDevices.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      validDevices.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
      mapRef.current.fitBounds(bounds);
    }
  }, [googleLoaded, validDevices]);

  // formatter fallback
  const defaultFormat = (v: string) => {
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };
  const fmt = formatDateTime ?? defaultFormat;

  const activeDevice = validDevices.find((d) => d.id === activeId) || null;

  // siapkan Location Time final untuk activeDevice:
  // const locationTimeStr =
  //   activeDevice?.time != null
  //     ? activeDevice?.utc != null
  //       ? computeTimePlusUtc(activeDevice.time, activeDevice.utc).display_enUS
  //       : fmt(activeDevice.time)
  //     : undefined;
  const activeTime = activeDevice?.time
    ? activeDevice?.utc != null
      ? computeTimePlusUtc(activeDevice.time, activeDevice.utc)
      : null
    : null;

  // existing fallback formatter:
  const locationTimeStr =
    activeDevice?.time != null
      ? activeTime
        ? activeTime.display_enUS
        : fmt(activeDevice.time)
      : undefined;

  if (!googleLoaded) {
    return <div style={mapContainerStyle} />;
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      zoom={8}
      center={defaultCenter}
      onLoad={onMapLoad}
    >
      {validDevices.map((d) => (
        <Marker
          key={String(d.id)}
          position={{ lat: d.lat, lng: d.lng }}
          icon={{
            url: getCustomIcon(d.username),
            scaledSize: new google.maps.Size(40, 40),
          }}
          onClick={() => setActiveId(d.id)}
        />
      ))}

      {activeDevice && (
        <InfoWindow
          position={{ lat: activeDevice.lat, lng: activeDevice.lng }}
          onCloseClick={() => setActiveId(null)}
        >
          <div>
            <p>
              <strong>Device ID:</strong>{" "}
              {activeDevice.id_device ?? activeDevice.id}
            </p>
            <p>
              <strong>User:</strong> {activeDevice.username}
            </p>
            {locationTimeStr && (
              <p>
                <strong>Location Time:</strong> {locationTimeStr}{" "}
                {activeTime && (
                  <small className="text-muted">({activeTime.utcLabel})</small>
                )}
              </p>
            )}
            {activeDevice.battery && (
              <p>
                <strong>Battery:</strong> {activeDevice.battery}%
              </p>
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default AllDevicesMap;
